'use client';

/* oxlint-disable react/only-export-components -- composable feature namespace mirrors shared-ui component APIs */

import { createContext, useContext, type ComponentProps, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Typography } from '@/shared-ui/atoms/typography';
import { Accordion } from '@/shared-ui/molecules/accordion';

type RootProps = ComponentProps<'section'> & {
    title?: string;
    children: ReactNode;
};

type AccordionProps = ComponentProps<typeof Accordion.Root>;
type ItemProps = ComponentProps<typeof Accordion.Item>;
type TriggerProps = ComponentProps<typeof Accordion.Trigger>;
type ContentProps = Omit<ComponentProps<typeof Accordion.Content>, 'forceMount'>;

const FaqAccordionContext = createContext(false);

const Root = ({ title, children, className, ...props }: RootProps) => (
    <section className={cn('w-full', className)} {...props}>
        {title?.trim() ? (
            <Typography variant="h3" className="font-normal">
                {title}
            </Typography>
        ) : null}
        {children}
    </section>
);
Root.displayName = 'Faq.Root';

const AccordionRoot = ({ className, ...props }: AccordionProps) => (
    <FaqAccordionContext.Provider value>
        <Accordion.Root className={cn('w-full', className)} {...props} />
    </FaqAccordionContext.Provider>
);
AccordionRoot.displayName = 'Faq.Accordion';

const Item = ({ className, ...props }: ItemProps) => {
    const isInsideFaqAccordion = useContext(FaqAccordionContext);
    if (!isInsideFaqAccordion) return null;

    return <Accordion.Item className={cn('border-foreground/20', className)} {...props} />;
};
Item.displayName = 'Faq.Item';

const Trigger = ({ className, ...props }: TriggerProps) => (
    <Accordion.Trigger variant="h4" weight="regular" className={cn('py-4', className)} {...props} />
);
Trigger.displayName = 'Faq.Trigger';

const Content = ({ className, accordionContentClassName, ...props }: ContentProps) => (
    <Accordion.Content
        className={className}
        accordionContentClassName={cn('text-body-md font-light ', accordionContentClassName)}
        {...props}
    />
);
Content.displayName = 'Faq.Content';

export const Faq = { Root, Accordion: AccordionRoot, Item, Trigger, Content } as const;
/* oxlint-enable react/only-export-components */
